import { useCallback } from 'react';
import { useDockviewApi } from './dockview-context.js';
import { DEFAULT_GROUP_ID, useAppStore, useActiveTabId, type PanelState } from './store.js';
import type { PanelComponentKey, PanelParams } from './components.js';

// Opens a Company Overview tab focused on the given entity inside the
// currently-active workspace tab.
//
// If a Company Overview panel already exists in the active tab, reuse
// it: switch the default link channel to the new entity (any linked
// Company Overview rewires automatically) and bring the panel forward.
//
// If none exists, add one as a floating panel and set the entity. The
// user can dock it where they want.
//
// Used by the entity-row context menu ("Company information") and the
// command palette ("Search panels, entities, actions" picker).

export function useOpenCompanyOverview(): (entityId: string) => void {
  const activeTabId = useActiveTabId();
  const apiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const setGroupEntity = useAppStore((s) => s.actions.setGroupEntity);
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);

  return useCallback(
    (entityId: string) => {
      if (!activeTabId) return;
      const api = apiRef.current;
      if (!api) return;

      // Look at the current dockview panel list for an already-open
      // Company Overview. Cheaper than wiring useTabPanels here — the
      // dockview side mirrors the store on this point.
      const existing = api.panels.find((p) => (p.api.component as string) === 'company-overview');
      if (existing) {
        // Panel-aware, not channel-hardcoded: the existing panel may be
        // linked to any colour channel (not just DEFAULT_GROUP_ID) or
        // pinned to its own entity. setPanelEntity resolves whichever of
        // those applies and updates it; writing straight to the default
        // channel silently no-ops when the panel is on another channel.
        setPanelEntity(activeTabId, existing.id, entityId);
        existing.api.setActive();
        return;
      }

      const newPanelId = addPanelToStore(activeTabId, {
        component: 'company-overview',
        title: 'Record',
      });
      const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
      const offset = 24 * cascade;
      api.addPanel<PanelParams>({
        id: newPanelId,
        component: 'company-overview' as PanelComponentKey,
        title: 'Record',
        params: { tabId: activeTabId, panelId: newPanelId },
        floating: {
          position: { left: 120 + offset, top: 80 + offset },
          width: 520,
          height: 400,
        },
      });
      setGroupEntity(activeTabId, DEFAULT_GROUP_ID, entityId);
    },
    [activeTabId, apiRef, addPanelToStore, setGroupEntity, setPanelEntity],
  );
}

// Opens a NEW, independent Company Overview window for the given entity — a
// fresh floating panel PINNED to that entity, so it doesn't rewire the shared
// default channel (i.e. it doesn't hijack an already-open Overview). Each call
// pops a new window; used by the map site-popup "Company information" button.
export function useOpenCompanyWindow(): (entityId: string) => void {
  const activeTabId = useActiveTabId();
  const apiRef = useDockviewApi();
  const addPanelToStore = useAppStore((s) => s.actions.addPanel);
  const pinPanel = useAppStore((s) => s.actions.pinPanel);
  const setPanelEntity = useAppStore((s) => s.actions.setPanelEntity);

  return useCallback(
    (entityId: string) => {
      if (!activeTabId) return;
      const api = apiRef.current;
      if (!api) return;

      const newPanelId = addPanelToStore(activeTabId, {
        component: 'company-overview',
        title: 'Record',
      });
      // Pin it to its own entity (independent of the default channel), then set
      // that entity — so this window shows the clicked company and nothing else
      // retargets it.
      pinPanel(activeTabId, newPanelId);
      setPanelEntity(activeTabId, newPanelId, entityId);

      const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
      const offset = 24 * cascade;
      api.addPanel<PanelParams>({
        id: newPanelId,
        component: 'company-overview' as PanelComponentKey,
        title: 'Record',
        params: { tabId: activeTabId, panelId: newPanelId },
        floating: {
          position: { left: 140 + offset, top: 90 + offset },
          width: 520,
          height: 420,
        },
      });
    },
    [activeTabId, apiRef, addPanelToStore, pinPanel, setPanelEntity],
  );
}

// Helper for tests / non-hook contexts.
export function findCompanyOverviewPanel(panels: PanelState[]): PanelState | undefined {
  return panels.find((p) => p.component === 'company-overview');
}
